import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { fmt } from '../utils/gst';
import toast from 'react-hot-toast';

function InvoiceDetail({ invId, active }) {
  const [showViewer, setShowViewer] = useState(false);
  const [viewerUrl, setViewerUrl] = useState(null);
  const { data: inv, isLoading } = useQuery({
    queryKey: ['invoice-detail', invId],
    queryFn: () => api.get(`/invoices/${invId}`).then(r => r.data),
    enabled: !!invId && active,
    staleTime: 60000,
  });

  function openInlineViewer() {
    api.get(`/invoices/${invId}/print`, { responseType: 'text' }).then(r => {
      const blob = new Blob([r.data], { type: 'text/html' });
      setViewerUrl(URL.createObjectURL(blob));
      setShowViewer(true);
    }).catch(() => alert('Failed to load invoice'));
  }

  if (isLoading) return <div style={{ padding: '12px 0', fontSize: 12, color: 'var(--text2)' }}>Loading…</div>;
  if (!inv) return null;

  const R = (v) => '₹' + parseFloat(v||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const Row = ({ label, value, bold, color }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--border)', fontWeight: bold ? 700 : 400, color: color || 'inherit', fontSize: 12 }}>
      <span style={{ color: bold ? 'inherit' : 'var(--text2)' }}>{label}</span>
      <span>{value}</span>
    </div>
  );

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '2px solid var(--border)' }}>
      <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
        {/* Left: meta */}
        <div style={{ fontSize: 12, minWidth: 180 }}>
          <div style={{ marginBottom: 4 }}><span style={{ color: 'var(--text2)' }}>Invoice No.: </span><strong>{inv.invoice_number}</strong></div>
          <div style={{ marginBottom: 4 }}><span style={{ color: 'var(--text2)' }}>Dated: </span>{inv.issued_at ? new Date(inv.issued_at).toLocaleDateString('en-IN') : '—'}</div>
          {inv.due_at && <div style={{ marginBottom: 4 }}><span style={{ color: 'var(--text2)' }}>Due: </span>{new Date(inv.due_at).toLocaleDateString('en-IN')}</div>}
          {inv.notes && <div style={{ marginTop: 8, fontStyle: 'italic', color: 'var(--text2)' }}>Note: {inv.notes}</div>}
        </div>
        {/* Right: tax breakdown */}
        <div style={{ flex: 1, maxWidth: 280 }}>
          <Row label="Taxable Amount" value={R(inv.subtotal)} />
          {parseFloat(inv.sgst_amount||0) > 0 && <Row label="SGST" value={R(inv.sgst_amount)} color="#1E7E4E" />}
          {parseFloat(inv.cgst_amount||0) > 0 && <Row label="CGST" value={R(inv.cgst_amount)} color="#1E7E4E" />}
          {parseFloat(inv.igst_amount||0) > 0 && <Row label="IGST" value={R(inv.igst_amount)} color="#1E7E4E" />}
          {parseFloat(inv.total_tax||0) > 0 && <Row label="Total Tax" value={R(inv.total_tax)} />}
          <Row label="Grand Total" value={R(inv.grand_total)} bold />
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <button className="btn-primary btn-sm" onClick={openInlineViewer}>📄 View Invoice</button>
      </div>

      {showViewer && viewerUrl && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', flexDirection: 'column' }}>
          <div style={{ background: '#fff', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong>Invoice {inv.invoice_number} — {inv.client_name}</strong>
            <button className="btn-ghost btn-sm" onClick={() => { setShowViewer(false); URL.revokeObjectURL(viewerUrl); setViewerUrl(null); }}>✕ Close</button>
          </div>
          <iframe src={viewerUrl} style={{ flex: 1, border: 'none', width: '100%' }} title="Invoice" />
        </div>
      )}
    </div>
  );
}

function agingColor(days) {
  if (days <= 7)  return { bg: '#F0FFF4', color: '#1E7E4E', label: 'Recent' };
  if (days <= 30) return { bg: '#FFFBEB', color: '#D97706', label: `${days}d` };
  return { bg: '#FFF0F0', color: '#C0392B', label: `${days}d overdue` };
}

// Matches the 0-30 / 31-60 / 60+ buckets used in the Outstanding tab's aging summary cards.
function invoiceAgingColor(days) {
  if (days <= 30) return { bg: '#FFFBEB', color: '#D97706' };
  if (days <= 60) return { bg: '#FFF7ED', color: '#EA580C' };
  return { bg: '#FFF0F0', color: '#C0392B' };
}

function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function shiftMonth(m, delta) {
  const [y, mo] = m.split('-').map(Number);
  const zeroBased = mo - 1 + delta;
  const newYear = y + Math.floor(zeroBased / 12);
  const newMonth = ((zeroBased % 12) + 12) % 12;
  return `${newYear}-${String(newMonth + 1).padStart(2, '0')}`;
}

function Note({ children, tone }) {
  const tones = {
    default: { bg: 'var(--surface2)', border: 'var(--border)', color: 'var(--text2)' },
    warn:    { bg: '#FFF7ED', border: '#FED7AA', color: '#9A3412' },
  };
  const t = tones[tone] || tones.default;
  return (
    <div style={{ fontSize: 12, color: t.color, marginBottom: 12, padding: '8px 14px', background: t.bg, border: `1px solid ${t.border}`, borderRadius: 6, lineHeight: 1.5 }}>
      {children}
    </div>
  );
}

export default function ReceivablesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [tab, setTab] = useState('overview');
  const [expandedId, setExpandedId] = useState(null);
  const toggleExpand = id => setExpandedId(v => v === id ? null : id);

  const { data, isLoading } = useQuery({
    queryKey: ['receivables', month],
    queryFn: () => api.get(`/reports/receivables?month=${month}`).then(r => r.data),
  });

  // Net Profit card has its own period (defaults to the main month picker, but can be widened into a range)
  const [npFrom, setNpFrom] = useState(month);
  const [npTo, setNpTo] = useState(month);
  useEffect(() => { setNpFrom(month); setNpTo(month); }, [month]);

  const { data: npData } = useQuery({
    queryKey: ['net-profit', npFrom, npTo],
    queryFn: () => api.get(`/reports/net-profit?fromMonth=${npFrom}&toMonth=${npTo}`).then(r => r.data),
  });

  const markPaidMutation = useMutation({
    mutationFn: id => api.patch(`/invoices/${id}/status`, { status: 'paid' }),
    onSuccess: () => {
      qc.invalidateQueries(['receivables', month]);
      qc.invalidateQueries(['invoices']);
      toast.success('Invoice marked as paid');
    },
    onError: () => toast.error('Failed to update'),
  });

  const monthLabel = m => new Date(m + '-01').toLocaleString('default', { month: 'long', year: 'numeric' });

  if (isLoading) return <div className="loading-screen"><div className="spinner" /></div>;

  const d = data || { overview: {}, unbilled: [], outstandingInvoices: [], collectedInvoices: [] };
  const { wlTotal=0, invoiced=0, collected=0, outstanding=0 } = d.overview;

  return (
    <div>
      <div className="section-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ marginBottom: 2 }}>Receivables & Cash Flow</h2>
          <div style={{ fontSize: 12, color: 'var(--text2)' }}>A monthly snapshot of work done, invoices raised, and cash collected.</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button className="btn-ghost btn-sm" onClick={() => setMonth(m => shiftMonth(m, -1))} aria-label="Previous month" style={{ fontSize: 16, padding: '2px 10px' }}>‹</button>
          <input type="month" className="form-input" value={month} onChange={e => setMonth(e.target.value)} style={{ width: 154, textAlign: 'center', fontWeight: 700 }} />
          <button className="btn-ghost btn-sm" onClick={() => setMonth(m => shiftMonth(m, 1))} aria-label="Next month" style={{ fontSize: 16, padding: '2px 10px' }}>›</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {[
          ['overview', 'Overview'],
          ['unbilled', `Unbilled Work${d.unbilled.length ? ` (${d.unbilled.length})` : ''}`],
          ['outstanding', `Outstanding${d.outstandingInvoices.length ? ` (${d.outstandingInvoices.length})` : ''}`],
          ['collected', 'Collected'],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12,
              fontWeight: tab===k ? 700 : 400,
              background: tab===k ? (k==='unbilled' && d.unbilled.length ? '#C0392B' : 'var(--accent)') : 'var(--surface)',
              color: tab===k ? '#fff' : 'var(--text2)', cursor: 'pointer' }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ─────────────────────────────────────────── */}
      {tab === 'overview' && (
        <div style={{ maxWidth: 760 }}>
          <Note>
            These three numbers measure <strong>different things</strong> and are not meant to add up to one another: what was <strong>done</strong> this month, what was <strong>billed</strong> this month (which can include older work), and what was <strong>paid</strong> this month.
          </Note>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            {/* Work Value */}
            <div className="card" style={{ padding: '16px 18px', background: 'var(--surface2)' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 700, letterSpacing: '0.03em' }}>WORK VALUE — LOGGED THIS MONTH</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text1)', margin: '6px 0' }}>{fmt(wlTotal)}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
                Value of all work logged in {monthLabel(month)}, at standard rates — whether or not it's been invoiced yet.
              </div>
            </div>

            {/* Invoiced */}
            <div className="card" style={{ padding: '16px 18px', background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
              <div style={{ fontSize: 11, color: '#1E40AF', fontWeight: 700, letterSpacing: '0.03em' }}>INVOICED — RAISED THIS MONTH</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#1D4ED8', margin: '6px 0' }}>{fmt(invoiced)}</div>
              <div style={{ fontSize: 11, color: '#3B82F6', lineHeight: 1.5 }}>
                Every invoice you raised in {monthLabel(month)} (draft, sent or paid) — may include billing for older work, not just this month's.
              </div>
            </div>

            {/* Collected */}
            <div className="card" style={{ padding: '16px 18px', background: '#F0FFF4', border: '1px solid #86EFAC' }}>
              <div style={{ fontSize: 11, color: '#166534', fontWeight: 700, letterSpacing: '0.03em' }}>COLLECTED — PAID THIS MONTH</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#15803D', margin: '6px 0' }}>{fmt(collected)}</div>
              <div style={{ fontSize: 11, color: '#166534', lineHeight: 1.5 }}>
                Of this month's invoices, the portion already marked Paid — real cash received.
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: outstanding > 0 ? '#FFF7ED' : 'var(--surface2)', border: `1px solid ${outstanding > 0 ? '#FED7AA' : 'var(--border)'}`, fontSize: 12, color: outstanding > 0 ? '#9A3412' : 'var(--text2)' }}>
            {outstanding > 0
              ? <><strong>{fmt(outstanding)}</strong> of this month's invoiced amount is still unpaid (draft or sent, not yet marked Paid).</>
              : 'Every invoice raised this month has been paid in full.'}
          </div>

          {/* Quick stats */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
            <div className="card" style={{ padding: '14px 18px' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>Clients with unbilled work</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: d.unbilled.length ? '#C0392B' : '#1E7E4E', margin: '4px 0' }}>
                {d.unbilled.length}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
                Clients whose logged work is newer than their last invoice (looks back 90 days, any month).
              </div>
              {d.unbilled.length > 0 && (
                <button className="btn-ghost btn-sm" style={{ marginTop: 6, color: '#C0392B' }} onClick={() => setTab('unbilled')}>
                  Review →
                </button>
              )}
            </div>
            <div className="card" style={{ padding: '14px 18px' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>Invoices awaiting payment</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: d.outstandingInvoices.length ? '#D97706' : '#1E7E4E', margin: '4px 0' }}>
                {d.outstandingInvoices.length}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
                Draft or sent invoices not yet marked Paid, across every month — not limited to {monthLabel(month)}.
              </div>
              {d.outstandingInvoices.length > 0 && (
                <button className="btn-ghost btn-sm" style={{ marginTop: 6, color: '#D97706' }} onClick={() => setTab('outstanding')}>
                  Follow up →
                </button>
              )}
            </div>
          </div>

          {/* Net Profit */}
          <div className="card" style={{ padding: '16px 18px', marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Net Profit</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text3)' }}>
                From
                <input type="month" className="form-input" value={npFrom} onChange={e => setNpFrom(e.target.value)} style={{ width: 130 }} />
                To
                <input type="month" className="form-input" value={npTo} onChange={e => setNpTo(e.target.value)} style={{ width: 130 }} />
              </div>
            </div>
            <Note>
              Net profit = Work Value earned in this period, minus expenses, salaries, and an estimated income-tax provision — the same definition as the Dashboard's P&L, just selectable by single month or a range here. Pick the same month in both fields for a single month, or widen "From" for a range.
            </Note>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>
                  NET PROFIT — {npFrom === npTo ? monthLabel(npFrom) : `${monthLabel(npFrom)} – ${monthLabel(npTo)}`}
                </div>
                <div style={{ fontSize: 24, fontWeight: 800, color: (npData?.period?.netProfit || 0) >= 0 ? 'var(--text1)' : '#C0392B', margin: '6px 0' }}>
                  {fmt(npData?.period?.netProfit || 0)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
                  {fmt(npData?.period?.revenue || 0)} work value − {fmt(npData?.period?.expenses || 0)} expenses − {fmt(npData?.period?.salaries || 0)} salaries − {fmt(npData?.period?.taxProvision || 0)} est. tax
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>
                  AVG NET PROFIT — FY TO DATE (through {monthLabel(npTo)})
                </div>
                <div style={{ fontSize: 24, fontWeight: 800, color: (npData?.fyToDate?.averageNetProfit || 0) >= 0 ? 'var(--text1)' : '#C0392B', margin: '6px 0' }}>
                  {fmt(npData?.fyToDate?.averageNetProfit || 0)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
                  Averaged over {npData?.fyToDate?.numMonths || 1} month{(npData?.fyToDate?.numMonths || 1) === 1 ? '' : 's'} since FY start ({npData?.fyToDate?.fyStartMonth ? monthLabel(npData.fyToDate.fyStartMonth) : '—'}).
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── UNBILLED TAB ─────────────────────────────────────────── */}
      {tab === 'unbilled' && (
        <div>
          <Note>
            <strong>What this is:</strong> clients whose logged work is dated after their most recent invoice — i.e. work that likely hasn't been billed yet. This list always looks at the last 90 days, regardless of the month selected above.
          </Note>
          {d.unbilled.length === 0 ? (
            <div className="card" style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
              <div style={{ fontWeight: 600, fontSize: 15 }}>All work is invoiced</div>
              <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 4 }}>No unbilled work found in the last 90 days</div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12, padding: '8px 14px', background: '#FFF8E7', borderRadius: 6, border: '1px solid #F59E0B' }}>
                ⚠️ Work older than 7 days needs attention.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {d.unbilled.map(u => {
                  const { bg, color, label } = agingColor(u.days_since_oldest);
                  return (
                    <div key={u.client_id} className="card" style={{ padding: '14px 18px', borderLeft: `4px solid ${color}` }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span style={{ fontWeight: 700, fontSize: 14 }}>{u.client_name}</span>
                            <span style={{ fontSize: 11, fontWeight: 700, background: bg, color, padding: '2px 8px', borderRadius: 12 }}>
                              {label}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                            {u.entry_count} work entr{u.entry_count === 1 ? 'y' : 'ies'} ·
                            Oldest: {fmtDate(u.oldest_work)} ·
                            Latest: {fmtDate(u.latest_work)}
                          </div>
                          {u.last_invoiced_date && (
                            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                              Last invoice raised: {fmtDate(u.last_invoiced_date)}
                            </div>
                          )}
                          {!u.last_invoiced_date && (
                            <div style={{ fontSize: 11, color: '#C0392B', marginTop: 2 }}>
                              Never invoiced
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: 18, fontWeight: 800, color }}>{fmt(u.estimated_value)}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>estimated base value</div>
                          <button className="btn-primary btn-sm" onClick={() => navigate('/invoices/new')}>
                            Raise Invoice →
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── OUTSTANDING TAB ──────────────────────────────────────── */}
      {tab === 'outstanding' && (
        <div>
          <Note>
            <strong>What this is:</strong> all invoices currently in Draft or Sent status (not yet marked Paid), across every month — not filtered to the month selected above.
          </Note>
          {d.outstandingInvoices.length === 0 ? (
            <div className="card" style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
              <div style={{ fontWeight: 600, fontSize: 15 }}>No outstanding invoices</div>
              <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 4 }}>All invoices have been paid or cancelled</div>
            </div>
          ) : (
            <>
              {/* Aging summary */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
                {[['0-30', '#D97706', '#FFFBEB'], ['31-60', '#EA580C', '#FFF7ED'], ['60+', '#C0392B', '#FFF0F0']].map(([bucket, color, bg]) => {
                  const inv = d.outstandingInvoices.filter(i => i.aging_bucket === bucket);
                  return (
                    <div key={bucket} className="card" style={{ padding: '12px 16px', background: bg, borderTop: `3px solid ${color}` }}>
                      <div style={{ fontSize: 11, color, fontWeight: 700 }}>{bucket} days</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color }}>{fmt(inv.reduce((s,i) => s+i.grand_total, 0))}</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>{inv.length} invoice{inv.length !== 1 ? 's' : ''}</div>
                    </div>
                  );
                })}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {d.outstandingInvoices.map(inv => {
                  const { bg, color } = invoiceAgingColor(inv.days_outstanding);
                  const isOpen = expandedId === inv.id;
                  return (
                    <div key={inv.id} className="card" style={{ padding: '14px 18px', borderLeft: `4px solid ${color}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                            <span style={{ fontWeight: 700, fontSize: 13 }}>{inv.client_name}</span>
                            <span style={{ fontSize: 10, color: 'var(--text3)' }}>#{inv.invoice_number}</span>
                            <span style={{ fontSize: 11, fontWeight: 600, background: bg, color, padding: '1px 7px', borderRadius: 10 }}>
                              {inv.days_outstanding}d outstanding
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text2)' }}>
                            Raised: {fmtDate(inv.issued_date)}
                            {inv.due_at && ` · Due: ${fmtDate(inv.due_at)}`}
                            {inv.status === 'draft' && <span style={{ marginLeft: 8, color: '#D97706', fontWeight: 600 }}>⚠️ Still in draft — not sent yet</span>}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: 16, fontWeight: 800, color }}>{fmt(inv.grand_total)}</div>
                        </div>
                        <button className="btn-ghost btn-sm" onClick={() => toggleExpand(inv.id)} style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ display: 'inline-block', transition: 'transform 0.3s ease', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                          {isOpen ? 'Hide' : 'Details'}
                        </button>
                        <button className="btn-primary btn-sm"
                          onClick={() => { if (window.confirm(`Mark invoice #${inv.invoice_number} as paid?`)) markPaidMutation.mutate(inv.id); }}
                          style={{ flexShrink: 0 }}>
                          Mark Paid
                        </button>
                      </div>
                      <div style={{ maxHeight: isOpen ? 600 : 0, overflow: 'hidden', transition: 'max-height 0.35s cubic-bezier(0.4,0,0.2,1)' }}>
                        <InvoiceDetail invId={inv.id} active={isOpen} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── COLLECTED TAB ────────────────────────────────────────── */}
      {tab === 'collected' && (
        <div>
          <Note>
            <strong>What this is:</strong> invoices raised in {monthLabel(month)} that have since been marked Paid (payment may have been recorded in a later month).
          </Note>
          {d.collectedInvoices.length === 0 ? (
            <div className="card" style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 13, color: 'var(--text2)' }}>No paid invoices in {monthLabel(month)}</div>
            </div>
          ) : (
            <>
              <div className="card" style={{ padding: '12px 18px', marginBottom: 12, display: 'flex', gap: 24 }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>Total collected this month</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#15803D' }}>
                    {fmt(d.collectedInvoices.reduce((s, i) => s + i.grand_total, 0))}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>Invoices paid</div>
                  <div style={{ fontSize: 20, fontWeight: 800 }}>{d.collectedInvoices.length}</div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {d.collectedInvoices.map(inv => {
                  const isOpen = expandedId === inv.id;
                  return (
                    <div key={inv.id} className="card" style={{ padding: '12px 18px', borderLeft: '4px solid #15803D' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{inv.client_name}</div>
                          <div style={{ fontSize: 11, color: 'var(--text2)' }}>
                            #{inv.invoice_number} · Raised: {fmtDate(inv.issued_date)} · Paid: {fmtDate(inv.paid_date) || '—'}
                          </div>
                        </div>
                        <div style={{ fontWeight: 700, fontSize: 16, color: '#15803D' }}>{fmt(inv.grand_total)}</div>
                        <button className="btn-ghost btn-sm" onClick={() => toggleExpand(inv.id)} style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ display: 'inline-block', transition: 'transform 0.3s ease', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                          {isOpen ? 'Hide' : 'Details'}
                        </button>
                      </div>
                      <div style={{ maxHeight: isOpen ? 600 : 0, overflow: 'hidden', transition: 'max-height 0.35s cubic-bezier(0.4,0,0.2,1)' }}>
                        <InvoiceDetail invId={inv.id} active={isOpen} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
